// Role permissions on the admin panel.
// Super Admin (1), Admin (2) and Coordinator (3) keep their existing fixed access, so every check
// below passes for them. Custom roles (Data Entry, ...) only get what their permissions allow.
// The backend enforces the same permissions on every API call; this only shapes the UI.

const SYSTEM_ROLES = [1, 2, 3];

export function getAdminUser() {
  try {
    return JSON.parse(localStorage.getItem('adminUser') || '{}') || {};
  } catch (e) {
    return {};
  }
}

export function isCustomRole(user = getAdminUser()) {
  const role = Number(user?.role);
  return Number.isInteger(role) && role > 0 && !SYSTEM_ROLES.includes(role);
}

/** True when the user holds any of the given permissions (a `*` segment matches anything). */
export function can(permissions, user = getAdminUser()) {
  if (!isCustomRole(user)) return true;
  const granted = Array.isArray(user?.permissions) ? user.permissions : [];
  const required = Array.isArray(permissions) ? permissions : [permissions];
  return required.some((req) => {
    if (!req.includes('*')) return granted.includes(req);
    const [prefix, suffix] = req.split('*');
    return granted.some((p) => p.startsWith(prefix) && p.endsWith(suffix));
  });
}

/** Drops DataGrid action columns (by `field`) whose permission the user lacks, e.g. { Delete: 'bookings.delete' }. */
export function filterActionColumns(columns, fieldPermissions, user = getAdminUser()) {
  return columns.filter((col) => !fieldPermissions[col.field] || can(fieldPermissions[col.field], user));
}

// Page path -> permissions (any of). First match wins; pages not listed are closed to custom roles.
const PAGE_RULES = [
  ['/', ['dashboard.view']],

  ['/countries', ['destinations.view']],
  ['/countries/addcountry', ['destinations.create']],
  ['/countries/:id', ['destinations.view']],

  ['/excursions', ['excursions.view']],
  ['/excursions/addexcursion', ['excursions.create']],
  ['/excursions/:id/duplicate', ['excursions.create']],
  ['/excursions/:id/gallery', ['excursions.view']],
  ['/excursions/:id', ['excursions.view']],

  ['/country-excursion', ['trips.view']],
  ['/country-excursion/add', ['trips.create']],
  ['/country-excursion/:id/duplicate', ['trips.create']],
  ['/country-excursion/:id', ['trips.view']],

  ['/package', ['packages.view']],
  ['/package/order', ['packages.edit']],
  ['/package/addpackage', ['packages.create']],
  ['/package/:id/duplicate', ['packages.create']],
  ['/package/:id', ['packages.view']],

  ['/booking', ['bookings.view']],
  ['/booking/addBooking', ['bookings.create']],
  ['/booking/import', ['bookings.create']],
  ['/booking/:id/update', ['bookings.edit']],
  ['/booking/:id', ['bookings.view']],

  ['/allpassengers', ['passengers.view']],
  ['/addpassengers', ['passengers.create']],
  ['/passenger/:id/addmember', ['passengers.create', 'passengers.edit']],
  ['/passenger/:id/editpassenger', ['passengers.edit']],
  ['/passenger/:id', ['passengers.view']],

  ['/guide', ['guides.view']],
  ['/guide/addguide', ['guides.create']],
  ['/guide/import', ['guides.create']],
  ['/guide/:id', ['guides.view']],

  ['/promotion', ['promotions.view']],
  ['/promotion/addpromotion', ['promotions.create']],
  ['/promotion/:id', ['promotions.view']],

  ['/feedback', ['surveys.view']],
  ['/feedback/addfeedback', ['surveys.create']],
  ['/feedback/reorder', ['surveys.edit']],
  ['/feedback/:id', ['surveys.view']],

  ['/userfeedback', ['user_feedback.view']],
  ['/userfeedback/:id', ['user_feedback.view']],

  ['/message', ['messages.view']],
  ['/message/sendmessage', ['messages.send']],
  ['/message/:id', ['messages.view']],

  ['/notifications', ['notifications.view', 'notifications.send', 'surveys.send']],

  ['/reports/excursion', ['reports.excursion.view']],
  ['/reports', ['reports.excursion.view']],
  ['/reports/excursion-attendance', ['reports.excursion_attendance.view']],
  ['/reports/excursion-summary', ['reports.excursion_summary.view']],
  ['/reports/guide-performance', ['reports.guide_performance.view']],
  ['/reports/survey-results', ['reports.survey_results.view']],
  ['/reports/survey-responses', ['reports.survey_responses.view']],
  ['/reports/destinations', ['reports.destination.view']],
  ['/reports/destinations/:countryId', ['reports.destination.view']],
  ['/reports/destinations/:countryId/guides/:guideId', ['reports.destination.view']],
  ['/reports/view/:id', ['reports.excursion.view']],
  ['/reports/:id', ['reports.excursion.view']],
  ['/trip-reports', ['reports.trip.view']],
  ['/trip-reports/:countryId/:departureDate/:guideId/view', ['reports.trip.view']],
  ['/trip-reports/:countryId/:departureDate/:guideId', ['reports.trip.edit']],
  ['/reports-by-user', ['reports.report_by_user.view']],
  ['/reports-by-user/:id', ['reports.report_by_user.view']],
];

const COMPILED_PAGE_RULES = PAGE_RULES.map(([path, perms]) => ({
  regex: new RegExp(`^${path.split('/').map((s) => (s.startsWith(':') ? '[^/]+' : s)).join('/')}/?$`),
  perms,
}));

/** Whether the current user may open the given admin panel page. */
export function canAccessPage(pathname, user = getAdminUser()) {
  if (!isCustomRole(user)) return true;
  const rule = COMPILED_PAGE_RULES.find((r) => r.regex.test(pathname));
  return Boolean(rule) && can(rule.perms, user);
}
